import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useTranslation } from '../context/LanguageContext';
import {
  verifyLedgerHashChainBrowser,
  canonicalJsonStringify,
  extractCanonicalLedgerPayload,
  LedgerAuditResult,
} from '../utils/browserCrypto';
import type { LedgerEntry } from '@shared/types';
import {
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  Download,
  FileJson,
  FileSpreadsheet,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Cpu,
  Lock,
  ArrowDownRight,
  ArrowUpRight,
  Layers,
} from 'lucide-react';

export const LedgerVerification: React.FC = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [auditResult, setAuditResult] = useState<LedgerAuditResult | null>(null);
  const [expandedBlock, setExpandedBlock] = useState<number | null>(null);
  const [tamperMode, setTamperMode] = useState<boolean>(false);
  const [tamperSequence, setTamperSequence] = useState<number>(1);
  const [tamperField, setTamperField] = useState<'amount' | 'note'>('amount');
  const [tamperDelta, setTamperDelta] = useState<string>('1');

  const fetchAndAuditLedger = async () => {
    try {
      setLoading(true);
      setVerifying(true);

      const ledgerRef = collection(db, 'ledger');
      const q = query(ledgerRef, orderBy('sequenceNumber', 'asc'));
      const snapshot = await getDocs(q);

      const items: LedgerEntry[] = [];
      snapshot.forEach((doc) => {
        items.push({ id: doc.id, ...doc.data() } as unknown as LedgerEntry);
      });

      setEntries(items);
      const result = await verifyLedgerHashChainBrowser(items);
      setAuditResult(result);
    } catch (err) {
      console.error('Failed to load and audit ledger:', err);
    } finally {
      setLoading(false);
      setVerifying(false);
    }
  };

  useEffect(() => {
    fetchAndAuditLedger();
  }, []);

  const runTamperTest = async () => {
    if (entries.length === 0) return;
    setVerifying(true);

    // Deep clone entries and alter target block
    const cloned = JSON.parse(JSON.stringify(entries)) as LedgerEntry[];
    const targetIdx = cloned.findIndex((e) => e.sequenceNumber === tamperSequence);
    if (targetIdx !== -1) {
      if (tamperField === 'amount') {
        cloned[targetIdx].amount += parseInt(tamperDelta || '1', 10);
      } else {
        cloned[targetIdx].note += ' [MODIFIED_TAMPER_TEST]';
      }
    }

    const result = await verifyLedgerHashChainBrowser(cloned);
    setAuditResult(result);
    setVerifying(false);
  };

  const resetAudit = async () => {
    setVerifying(true);
    const result = await verifyLedgerHashChainBrowser(entries);
    setAuditResult(result);
    setVerifying(false);
  };

  const exportJson = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(entries, null, 2));
    const dlAnchorElem = document.createElement('a');
    dlAnchorElem.setAttribute('href', dataStr);
    dlAnchorElem.setAttribute('download', `fhpf_cryptographic_ledger_${Date.now()}.json`);
    dlAnchorElem.click();
  };

  const exportCsv = () => {
    if (entries.length === 0) return;
    const headers = ['sequenceNumber', 'type', 'amount', 'refId', 'note', 'createdBy', 'createdAt', 'prevHash', 'entryHash'];
    const rows = entries.map((e) => [
      e.sequenceNumber,
      e.type,
      e.amount,
      `"${e.refId}"`,
      `"${(e.note || '').replace(/"/g, '""')}"`,
      `"${e.createdBy}"`,
      e.createdAt,
      e.prevHash,
      e.entryHash,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `fhpf_ledger_${Date.now()}.csv`);
    link.click();
  };

  return (
    <div className="space-y-6 pb-24 sm:pb-8">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-navy-900 to-navy-950 text-white border border-navy-800 shadow-md">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-1">
            <Lock className="w-4 h-4" />
            <span>Public Trust Architecture</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold">{t('ledger.title')}</h1>
          <p className="text-xs sm:text-sm text-gray-400 mt-0.5">{t('ledger.subtitle')}</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchAndAuditLedger}
            disabled={verifying}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-primary-600 hover:bg-primary-500 text-white transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${verifying ? 'animate-spin' : ''}`} />
            <span>{t('ledger.verifyBtn')}</span>
          </button>
          <button
            onClick={exportJson}
            disabled={entries.length === 0}
            className="flex items-center gap-1 px-2.5 py-2 rounded-xl text-xs font-medium bg-navy-800 hover:bg-navy-700 text-gray-200 border border-navy-700 transition-colors"
            title={t('ledger.exportJson')}
          >
            <FileJson className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">JSON</span>
          </button>
          <button
            onClick={exportCsv}
            disabled={entries.length === 0}
            className="flex items-center gap-1 px-2.5 py-2 rounded-xl text-xs font-medium bg-navy-800 hover:bg-navy-700 text-gray-200 border border-navy-700 transition-colors"
            title={t('ledger.exportCsv')}
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">CSV</span>
          </button>
        </div>
      </div>

      {/* Verification Status Alert */}
      {auditResult && (
        <div
          className={`p-4 sm:p-5 rounded-2xl border transition-all ${
            auditResult.valid
              ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800 text-emerald-950 dark:text-emerald-200'
              : 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800 text-red-950 dark:text-red-200'
          }`}
        >
          <div className="flex items-start gap-3">
            {auditResult.valid ? (
              <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 shrink-0 mt-0.5 animate-pulse" />
            )}
            <div className="flex-1">
              <h3 className="text-base sm:text-lg font-bold">
                {auditResult.valid ? t('ledger.statusValid') : t('ledger.statusInvalid')}
              </h3>
              <p className="text-xs sm:text-sm mt-1 opacity-90">
                {auditResult.valid
                  ? `Cryptographic proof: all ${auditResult.verifiedCount} consecutive blocks possess valid SHA-256 linkages back to Genesis (sequence #1). Zero records altered.`
                  : `Discontinuity alert at Sequence #${auditResult.brokenAtSequence}: ${auditResult.error}`}
              </p>
              {!auditResult.valid && auditResult.expectedHash && (
                <div className="mt-2 text-xs font-mono bg-red-100 dark:bg-red-900/60 p-2 rounded-lg overflow-x-auto">
                  <div>Expected SHA-256: {auditResult.expectedHash}</div>
                  <div>Detected SHA-256: {auditResult.actualHash}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Financial Metrics Cards */}
      {auditResult && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <div className="p-4 rounded-xl bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 shadow-sm">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              <Layers className="w-3.5 h-3.5 text-primary-500" />
              <span>{t('ledger.totalBlocks')}</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {auditResult.verifiedCount}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">Consecutive SHA-256</div>
          </div>

          <div className="p-4 rounded-xl bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 shadow-sm">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              <ArrowDownRight className="w-3.5 h-3.5 text-emerald-500" />
              <span>{t('ledger.totalCredited')}</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              ৳{auditResult.totalCredited.toLocaleString()}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">Contributions + Fees</div>
          </div>

          <div className="p-4 rounded-xl bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 shadow-sm">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              <ArrowUpRight className="w-3.5 h-3.5 text-amber-500" />
              <span>{t('ledger.totalDebited')}</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-amber-600 dark:text-amber-400">
              ৳{auditResult.totalDebited.toLocaleString()}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">Expenses + Land</div>
          </div>

          <div className="p-4 rounded-xl bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 shadow-sm">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              <ShieldCheck className="w-3.5 h-3.5 text-primary-500" />
              <span>{t('ledger.netBalance')}</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-primary-600 dark:text-primary-400">
              ৳{auditResult.netTreasuryBalance.toLocaleString()}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">Authoritative Net</div>
          </div>
        </div>
      )}

      {/* Tamper Simulation Drawer */}
      <div className="p-4 rounded-xl bg-gray-50 dark:bg-navy-900/60 border border-gray-200 dark:border-navy-800">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Cpu className="w-4 h-4 text-primary-500" />
            <span className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
              {t('ledger.tamperTest')}
            </span>
          </div>
          <button
            onClick={() => setTamperMode(!tamperMode)}
            className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-medium"
          >
            {tamperMode ? 'Hide Simulator' : 'Test Cryptographic Defense'}
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{t('ledger.tamperTestDesc')}</p>

        {tamperMode && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-navy-800 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Target Seq:</span>
              <select
                value={tamperSequence}
                onChange={(e) => setTamperSequence(parseInt(e.target.value, 10))}
                className="px-2 py-1 rounded-lg text-xs bg-white dark:bg-navy-800 border border-gray-300 dark:border-navy-700"
              >
                {entries.map((e) => (
                  <option key={e.sequenceNumber} value={e.sequenceNumber}>
                    #{e.sequenceNumber} ({e.type} ৳{e.amount})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Alter:</span>
              <select
                value={tamperField}
                onChange={(e) => setTamperField(e.target.value as 'amount' | 'note')}
                className="px-2 py-1 rounded-lg text-xs bg-white dark:bg-navy-800 border border-gray-300 dark:border-navy-700"
              >
                <option value="amount">Amount (+৳{tamperDelta})</option>
                <option value="note">Note Text</option>
              </select>
            </div>

            <button
              onClick={runTamperTest}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors"
            >
              Simulate Alteration
            </button>
            <button
              onClick={resetAudit}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-200 hover:bg-gray-300 dark:bg-navy-700 dark:hover:bg-navy-600 text-gray-700 dark:text-gray-200 transition-colors"
            >
              Restore & Re-Audit
            </button>
          </div>
        )}
      </div>

      {/* Block-by-Block Visual Ledger Timeline */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
            Sequential Blockchain View ({entries.length} Blocks)
          </h2>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">Loading and verifying blocks...</div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">No ledger entries recorded yet.</div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => {
              const isExpanded = expandedBlock === entry.sequenceNumber;
              const canonicalPayload = extractCanonicalLedgerPayload(entry);
              const canonicalJson = canonicalJsonStringify(canonicalPayload);

              return (
                <div
                  key={entry.sequenceNumber}
                  className="p-4 rounded-xl bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 shadow-sm transition-all"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-950/60 text-primary-600 dark:text-primary-400 flex items-center justify-center font-bold text-xs border border-primary-200 dark:border-primary-800">
                        #{entry.sequenceNumber}
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900 dark:text-white text-sm">
                            ৳{entry.amount.toLocaleString()}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-2xs font-semibold uppercase tracking-wider bg-gray-100 dark:bg-navy-800 text-gray-700 dark:text-gray-300">
                            {entry.type}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {entry.note || entry.refId} • {new Date(entry.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setExpandedBlock(isExpanded ? null : entry.sequenceNumber)}
                      className="flex items-center gap-1 self-start sm:self-auto text-xs text-primary-600 dark:text-primary-400 hover:underline font-medium"
                    >
                      <span>{isExpanded ? 'Hide Hashes' : 'Inspect SHA-256'}</span>
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  {/* Expanded Cryptographic Inspection */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-navy-800 space-y-2 text-xs">
                      <div>
                        <span className="text-gray-400 font-medium block mb-0.5">{t('ledger.prevHash')}:</span>
                        <code className="block p-2 rounded bg-gray-100 dark:bg-navy-950 font-mono text-2xs break-all text-gray-700 dark:text-gray-300">
                          {entry.prevHash}
                        </code>
                      </div>

                      <div>
                        <span className="text-gray-400 font-medium block mb-0.5">{t('ledger.entryHash')}:</span>
                        <code className="block p-2 rounded bg-emerald-50 dark:bg-emerald-950/40 font-mono text-2xs break-all text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                          {entry.entryHash}
                        </code>
                      </div>

                      <div>
                        <span className="text-gray-400 font-medium block mb-0.5">{t('ledger.canonicalPayload')}:</span>
                        <pre className="p-2 rounded bg-gray-100 dark:bg-navy-950 font-mono text-2xs overflow-x-auto text-gray-600 dark:text-gray-400">
                          {canonicalJson}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
